import { nanoid } from 'nanoid';
import { AlbumDocument, Spread, DOC_VERSION, type AlbumDocument as AlbumDocumentT, type AlbumTheme as AlbumThemeT, type Background, type Spread as SpreadT, type Element } from './schema';
import { FORMAT_PRESETS, PAGE_COUNT_LIMITS, defaultSpineWidthMm, type FormatId } from './formats';
import { CalendarOptions } from './calendar-options';
import { calendarMonthNumbers, populateCalendar } from './calendar';

export const newId = (): string => nanoid(12);

export const PT_PER_MM = 72 / 25.4;
export const MM_PER_PT = 25.4 / 72;
export const mmToPx = (mm: number, dpi: number) => (mm / 25.4) * dpi;
export const pxToMm = (px: number, dpi: number) => (px / dpi) * 25.4;

/** Spread size in mm for inner spreads. */
export function spreadSize(doc: Pick<AlbumDocumentT, 'page'>) {
  return { widthMm: doc.page.widthMm * 2, heightMm: doc.page.heightMm };
}
/** Cover strip size in mm: back + spine + front. */
export function coverSize(doc: Pick<AlbumDocumentT, 'page' | 'cover'>) {
  return { widthMm: doc.page.widthMm * 2 + doc.cover.spineWidthMm, heightMm: doc.page.heightMm };
}

/** Inner page count as a reader sees it (single spreads count as 1 page). */
export function innerPageCount(doc: Pick<AlbumDocumentT, 'spreads'>): number {
  return doc.spreads.reduce((n, s) => n + (s.single ? 1 : 2), 0);
}

/**
 * Which side the book's own reading direction puts the first page on.
 *
 * In a left-to-right book the spine is on the left and the recto — the front of a leaf, page 1 —
 * falls on the RIGHT. A right-to-left book is the mirror image: the spine is on the right and the
 * recto falls on the LEFT, which is why Hebrew books carry odd page numbers on the left-hand page.
 */
export function firstPageSide(doc: Pick<AlbumDocumentT, 'cover'>): 'left' | 'right' {
  return doc.cover.binding === 'rtl' ? 'left' : 'right';
}

/** Reading direction of spine text, resolving null to the binding's convention. */
export function spineTextDirection(doc: Pick<AlbumDocumentT, 'cover'>): 'down' | 'up' {
  return doc.cover.spineTextDirection ?? (doc.cover.binding === 'rtl' ? 'up' : 'down');
}

/** Frame rotation that makes a horizontal text box read along the spine in `direction`. */
export function spineRotationDeg(direction: 'down' | 'up'): number {
  return direction === 'down' ? 90 : -90;
}

/**
 * Enforce the book invariant: with `firstPageSingle`, the first and last spreads each hold a single
 * page, on the sides the binding direction dictates (see `firstPageSide`).
 */
export function normalizeSingles(doc: AlbumDocumentT): AlbumDocumentT {
  const last = doc.spreads.length - 1;
  const first = firstPageSide(doc);
  const wanted = (i: number): SpreadT['single'] => {
    if (!doc.firstPageSingle || doc.spreads.length < 2) return null;
    if (i === 0) return first;
    if (i === last) return first === 'right' ? 'left' : 'right';
    return null;
  };
  let changed = false;
  // Structural sharing: spreads whose `single` already matches keep their identity.
  const spreads = doc.spreads.map((s, i) => {
    const single = wanted(i);
    if (s.single === single) return s;
    changed = true;
    return { ...s, single };
  });
  return changed ? { ...doc, spreads } : doc;
}

/** A blank spread. `background` is what the album's style paints on a new page; omitted = white. */
export function emptySpread(background?: Background | null): SpreadT {
  return Spread.parse({ id: newId(), ...(background ? { background } : {}) });
}

/**
 * Album page counts are always EVEN (docs/00-decisions.md — "10–120 inner pages, even numbers").
 * An odd request is rounded UP to the next even number — never silently truncated, which would
 * hand the caller an album one page shorter than it asked for — and then clamped to the limits.
 */
export function evenInnerPages(n: number): number {
  const even = Number.isFinite(n) ? Math.ceil(Math.ceil(n) / 2) * 2 : PAGE_COUNT_LIMITS.min;
  return Math.min(PAGE_COUNT_LIMITS.max, Math.max(PAGE_COUNT_LIMITS.min, even));
}

export interface CreateAlbumOptions {
  calendar?: CalendarOptions;
  format: FormatId;
  /** required when format === 'custom' */
  page?: { widthMm: number; heightMm: number };
  innerPages?: number;
  title?: string;
  lang?: 'he' | 'en';
  /** Style by id only: palette, fonts and backgrounds stay at the schema defaults. Prefer `theme`. */
  themeId?: string;
  /**
   * The whole style, as `themeToAlbumTheme` in @freealbum/layouts resolves it. The album is created
   * wearing it: its palette and fonts, its cover background, and every page painted with its page
   * background — so picking a style in the wizard is visible on the first screen of the editor.
   */
  theme?: Partial<AlbumThemeT> & { id: string };
  /** where it will be printed, when that is already known (the wizard asks first) */
  print?: { service?: 'none' | 'prestophoto' | 'blurb' | 'lulu'; binding?: 'softcover' | 'hardcover' };
}

/** Build a fresh, valid document. Spread count derives from innerPages and firstPageSingle. */
export function createAlbumDocument(opts: CreateAlbumOptions): AlbumDocumentT {
  const page = opts.format === 'custom'
    ? opts.page ?? { widthMm: 200, heightMm: 200 }
    : { widthMm: FORMAT_PRESETS[opts.format].widthMm, heightMm: FORMAT_PRESETS[opts.format].heightMm };
  const calendar = opts.calendar ? CalendarOptions.parse(opts.calendar) : undefined;
  const innerPages = evenInnerPages(calendar ? calendarMonthNumbers(calendar).length : opts.innerPages ?? 20);
  // with firstPageSingle: pages = 2*(spreads-2) + 2  => spreads = pages/2 + 1
  const spreadCount = innerPages / 2 + 1;
  const theme = opts.theme ?? { id: opts.themeId ?? 'family' };
  const spreads: SpreadT[] = Array.from({ length: spreadCount }, () => emptySpread(theme.pageBackground));
  const doc = AlbumDocument.parse({
    version: DOC_VERSION,
    format: opts.format,
    page,
    meta: { title: opts.title ?? '', lang: opts.lang ?? 'he' },
    print: opts.print ?? {},
    theme,
    cover: {
      spineWidthMm: defaultSpineWidthMm(innerPages),
      binding: (opts.lang ?? 'he') === 'he' ? 'rtl' : 'ltr',
      ...(theme.coverBackground ? { background: theme.coverBackground } : {}),
    },
    spreads,
  });
  const normalized = normalizeSingles(doc);
  return calendar ? populateCalendar(normalized, calendar) : normalized;
}

/** All photo ids referenced anywhere (elements, backgrounds, cover). */
export function referencedPhotoIds(doc: AlbumDocumentT): Set<string> {
  const ids = new Set<string>();
  const visit = (els: Element[], bg: unknown[]) => {
    for (const el of els) if (el.type === 'photo' && el.photoId) ids.add(el.photoId);
    for (const b of bg) if (b && typeof b === 'object' && (b as { type?: string }).type === 'photo') ids.add((b as { photoId: string }).photoId);
  };
  visit(doc.cover.elements, [doc.cover.background, doc.cover.spineBackground]);
  for (const s of doc.spreads) visit(s.elements, [s.background, s.backgroundRight]);
  return ids;
}

/** Parse + validate untrusted JSON into a document, applying defaults. Throws ZodError. */
export function parseAlbumDocument(input: unknown): AlbumDocumentT {
  return normalizeSingles(AlbumDocument.parse(input));
}
